import React from "react";
import { Box } from "@chakra-ui/react";
import { ProductFeaturesHero } from "components/hero";
import { remCalc } from "theme/utils";
import { FadeIn } from "ui/animations";
import { LottieWithStaticSafariImage } from "ui/lottie";
import heroImage from "../../../../public/assets/platform/identity-resolution/hero.png";
import heroFeatureImage1 from "../../../../public/assets/platform/identity-resolution/hero-product-feature-1.png";
import heroFeatureImage2 from "../../../../public/assets/platform/identity-resolution/hero-product-feature-2.png";
import heroFeatureImage3 from "../../../../public/assets/platform/identity-resolution/hero-product-feature-3.png";
import idrHeroAnimation from "./idr-hero-animation.json";

function HeroAnimation() {
  return (
    <Box
      borderRadius="bigger"
      border="1px"
      borderColor="brand.border.extraLight"
      overflow="hidden"
      bg="brand.backgroundSecondary.light"
    >
      <FadeIn duration={0.15}>
        <LottieWithStaticSafariImage
          isPriority
          ratio={1320 / 508}
          animationData={idrHeroAnimation}
          image={heroImage}
          imagePlaceholder="blur"
          alt="User data flowing from a data warehouse, into Hightouch where unified profiles are created, and finally into various tools."
          containerProps={{ pt: "tiny" }}
        />
      </FadeIn>
    </Box>
  );
}

type Props = {
  eyebrow?: string;
  title: string;
  description: string;
};

export function IdrHero({ eyebrow, title, description }: Props) {
  return (
    <ProductFeaturesHero
      theme="light"
      variant="blue"
      eyebrow={eyebrow}
      title={title}
      description={description}
      mobileFeatureTextMinH={{ base: remCalc(135), md: remCalc(108) }}
      media={<HeroAnimation />}
      productFeatures={[
        {
          id: "idr-hero-feature-1",
          title: "Use all of your data",
          description: "Stitch together identity profiles from any data you’ve collected in your data warehouse.",
          mobileImage: {
            src: heroFeatureImage1,
            alt: "Illustration showing users, events, and product from your data warehouse flowing out as a single line of data.",
          },
        },
        {
          id: "idr-hero-feature-2",
          title: "Get complete and unified profiles",
          description:
            "Define the profiles you want to create from your complete data, and store them directly in your data warehouse.",
          mobileImage: {
            src: heroFeatureImage2,
            alt: "Illustration showing a single line of data flowing into a unified profile, including name, email address, phone number, IP address, and customer ID. From the profile, many lines of data are flowing out.",
          },
        },
        {
          id: "idr-hero-feature-3",
          title: "Power a Customer 360 in every tool",
          description:
            "Keep all of your tools in sync with a complete profile of every customer, product, account, and anything else important to your business.",
          mobileImage: {
            src: heroFeatureImage3,
            alt: "An illustration showing many lines of data flowing into different tools, including SaaS tools, advertising, analytics, finance and ERP, internal comms, and app databases.",
          },
        },
      ]}
    />
  );
}
